import React, { useState, useEffect } from "react";
import { Resume } from "./types";
import { ThemeProvider } from "./context/ThemeContext";
import { ProfileProvider } from "./context/ProfileContext";
import Sidebar from "./components/Sidebar";
import SectionTabs from "./components/SectionTabs";
import Skills from "./components/Skills";
import Certificates from "./components/Certificates";
import Experiences from "./components/Experiences";
import Projects from "./components/Projects";
import PrintLayout from "./components/PrintLayout";

type SectionId = "skills" | "certs" | "experiences" | "projects";

export default function App() {
  const [resume, setResume] = useState<Resume | null>(null);
  const [activeSection, setActiveSection] = useState<SectionId>("skills");

  useEffect(() => {
    fetch("resume.json")
      .then((r) => r.json())
      .then((data: Resume) => setResume(data));
  }, []);

  if (!resume) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0c0c0e]">
        <div className="w-5 h-5 border-2 border-neutral-300 border-t-neutral-900 dark:border-neutral-700 dark:border-t-neutral-200 rounded-full animate-spin" />
      </div>
    );
  }

  const tabs: { id: SectionId; label: string }[] = [
    { id: "skills", label: "Compétences" },
    ...(resume.certificates.length > 0
      ? [{ id: "certs" as SectionId, label: "Certifications" }]
      : []),
    { id: "experiences", label: "Expériences" },
    { id: "projects", label: "Projets" },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "skills":
        return <Skills skills={resume.skills} />;
      case "certs":
        return <Certificates certificates={resume.certificates} />;
      case "experiences":
        return (
          <Experiences
            experiences={resume.references}
            skills={resume.skills}
            title="Expériences"
          />
        );
      case "projects":
        return <Projects projects={resume.projects} skills={resume.skills} />;
    }
  };

  return (
    <ThemeProvider>
      <ProfileProvider>
        <div className="min-h-screen bg-white dark:bg-[#0c0c0e] transition-colors duration-500">
          {/* Mobile layout */}
          <div className="lg:hidden">
            <div className="px-5 py-8 space-y-8">
              <Sidebar informations={resume.basics} interests={resume.interests} />
              <SectionTabs tabs={tabs} active={activeSection} onChange={setActiveSection} />
              <div className="animate-fade-in" key={activeSection}>
                {renderSection()}
              </div>
              <footer className="text-center text-[11px] text-neutral-400 dark:text-neutral-600 pt-4 border-t border-neutral-100 dark:border-white/[0.04]">
                Dernière mise à jour : {process.env.LAST_BUILD}
              </footer>
            </div>
          </div>

          {/* Desktop layout: fixed sidebar + scrollable content */}
          <div className="hidden lg:flex min-h-screen">
            {/* Sidebar */}
            <aside className="fixed top-0 left-0 w-[360px] h-screen overflow-y-auto border-r border-neutral-100 dark:border-white/[0.04] bg-neutral-50/80 dark:bg-white/[0.02] backdrop-blur-sm">
              <div className="p-8 flex flex-col h-full">
                <Sidebar informations={resume.basics} interests={resume.interests} />
                <footer className="mt-auto pt-6 text-[10px] text-neutral-400 dark:text-neutral-600">
                  Dernière mise à jour : {process.env.LAST_BUILD}
                </footer>
              </div>
            </aside>

            {/* Main content */}
            <main className="ml-[360px] flex-1 min-h-screen">
              {/* Sticky section tabs */}
              <div className="sticky top-0 z-20 bg-white/80 dark:bg-[#0c0c0e]/80 backdrop-blur-md border-b border-neutral-100 dark:border-white/[0.04]">
                <div className="px-10 pt-4 pb-0">
                  <SectionTabs tabs={tabs} active={activeSection} onChange={setActiveSection} />
                </div>
              </div>

              {/* Section content — full width */}
              <div className="px-10 py-8">
                <div key={activeSection} className="animate-fade-in">
                  {renderSection()}
                </div>
              </div>
            </main>
          </div>

          {/* Print layout — dedicated recruiter-friendly format */}
          <div className="hidden print-layout">
            <PrintLayout resume={resume} />
          </div>
        </div>
      </ProfileProvider>
    </ThemeProvider>
  );
}
